import { listBlobs } from '~/storage/blobStore'
import type { SolaDatabase } from '~/storage/db'
import type { StorageError } from '~/storage/errors'
import { notifyWritten } from '~/storage/invalidation'
import { deleteAllPhotos, listPhotos } from '~/storage/repositories/photoRepository'
import { STORE, type StoreName } from '~/storage/schema'
import { withTransaction } from '~/storage/transactions'
import { ok, type Result } from '~/utils/result'

/**
 * Delete one kind of record without touching the rest (Checkpoint 4.3).
 *
 * `privacy-and-storage.md` requires deletion of individual entries, all photos,
 * and all app data. The full reset (D-067) covers the last; this covers the
 * middle ground, which until now had no path at all — a user who wanted their
 * test weigh-ins gone had to destroy everything.
 *
 * **Structural records are deliberately absent.** Profile, settings, project,
 * phase and goal are not deletable here: removing a phase would orphan every
 * weekly review that references it, and removing the project would orphan the
 * phase. Those are the full reset's business, and it clears them together.
 *
 * **A category deletes atomically or not at all.** Photos own two blobs each, a
 * review owns the opinion it was given, and a half-completed delete leaves
 * exactly the orphan states this project has already been bitten by once.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS LIST IS HAND-WRITTEN AND THE FULL RESET IS NOT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `ALL_STORE_NAMES` derives from `CURRENT_SCHEMA`, so a new store is covered by
 * the full reset the moment it is declared. **The categories below are not
 * derived from anything**, so a new store is silently absent from them —
 * indistinguishable from a deliberate decision unless someone writes the
 * decision down. Check this list against the schema whenever a store is added.
 *
 * **Routine anchors (C6.5) are deliberately absent, and this is the record of
 * that.** An anchor is a *preference* — no rule reads one, nothing is judged
 * against it, and a missed one writes nothing (D-099) — so deleting it is a
 * settings question rather than a data question. It belongs to the narrower
 * settings-only reset recorded as **L-013**, of which it is now the first
 * concrete member. Offering "delete my routine times" beside "delete my photos"
 * would put a display setting in a list of health records.
 *
 * **`nutritionPlans` and `dailyChecks` (C6.4) are absent and should not be**;
 * that gap is recorded as a technical risk in `docs/decisions.md` and is not
 * closed here.
 */

export type DeletionCategory =
  | 'weighIns'
  | 'measurementSessions'
  | 'photos'
  | 'weeklyReviews'
  | 'strengthSets'
  /* Added in the same change that started storing proposals (D-094 §5), which is
     the whole point of it: this list is hand-written, so a new store is silently
     absent from it, and that is how `nutritionPlans` and `dailyChecks` became
     deletable only by destroying everything. Decided rather than discovered. */
  | 'aiProposals'
  /* Added in the same change that created the store (D-105 §12), on exactly the
     precedent above. **The schedule itself is deliberately not here**: it is plan
     structure rather than collected evidence, so it belongs to the full reset by
     the same reasoning D-085 applied to `domainGoals`. Deleting the weeks leaves
     the plan intact, which is the distinction that makes this category safe to
     offer beside "delete my photos". */
  | 'trainingSessions'

export interface DeletionCategoryDescription {
  readonly category: DeletionCategory
  readonly label: string
  /** What goes with it. Shown before the user arms the delete, never after. */
  readonly alsoRemoves: string | undefined
  /** What stops working afterwards. Stated plainly rather than discovered. */
  readonly consequence: string
}

export const DELETION_CATEGORIES: readonly DeletionCategoryDescription[] = [
  {
    category: 'weighIns',
    label: 'Daily weigh-ins',
    alsoRemoves: undefined,
    consequence:
      'Your seven-day average and every weight trend are rebuilt from what is left, so the ' +
      'weekly recommendation may change or stop being available.',
  },
  {
    category: 'measurementSessions',
    label: 'Measurement sessions',
    alsoRemoves: undefined,
    consequence:
      'Waist trends and the Navy circumference estimate are rebuilt from what is left, and the ' +
      'estimate disappears if no session remains.',
  },
  {
    category: 'photos',
    label: 'Progress photos',
    alsoRemoves: 'the full-size image and the thumbnail for each one',
    consequence: 'Every comparison goes with them. Weights, measurements and reviews are kept.',
  },
  {
    category: 'weeklyReviews',
    label: 'Weekly reviews',
    alsoRemoves: 'the opinion Project Sola gave for each week',
    consequence:
      'The record of what Project Sola advised, and when, is gone. A project completion summary ' +
      'will report fewer weeks reviewed.',
  },
  {
    category: 'strengthSets',
    label: 'Recorded sets',
    alsoRemoves: undefined,
    consequence:
      'Estimated 1RM history goes with them. The lifts you defined are kept, so you can carry ' +
      'on recording sets against them.',
  },
  {
    category: 'aiProposals',
    label: 'Second opinions',
    alsoRemoves: undefined,
    /* All of them or none. Removing a single disagreement from a run of
       agreements would make the history a curated argument rather than a
       record, which is why the repository offers no per-record delete. */
    consequence:
      'Every second opinion you have been given is removed, including the ones you agreed with. ' +
      'Nothing Project Sola decided changes — a second opinion never altered your plan.',
  },
  {
    category: 'trainingSessions',
    label: 'Recorded training weeks',
    alsoRemoves: undefined,
    consequence:
      'The record of which sessions you completed, moved or skipped is removed. Your training ' +
      'schedule is kept, so the weeks ahead are unchanged.',
  },
]

/** How many records each category holds right now. Read live, never estimated. */
export type DeletionCounts = Readonly<Record<DeletionCategory, number>>

export interface DeletionPreview {
  readonly counts: DeletionCounts
  /** Bytes the photos hold, so "free up space" is a real figure. */
  readonly photoBytes: number
}

export async function previewSelectiveDeletion(
  db: SolaDatabase,
): Promise<Result<DeletionPreview, StorageError>> {
  const photos = await listPhotos(db)
  if (!photos.ok) return photos

  const blobs = await listBlobs(db)
  if (!blobs.ok) return blobs

  /* Only the blobs these photos actually own. Counting every blob would report
     an attachment's bytes as a photo's, and the figure is shown next to a
     destructive action. */
  const owned = new Set(photos.value.flatMap((photo) => [photo.blobId, photo.thumbnailBlobId]))
  const photoBytes = blobs.value
    .filter((entry) => owned.has(entry.blobId as never))
    .reduce((sum, entry) => sum + (entry.byteSize ?? entry.blob.size), 0)

  return ok({
    counts: {
      weighIns: await count(db, STORE.weightEntries),
      measurementSessions: await count(db, STORE.measurementSessions),
      photos: photos.value.length,
      weeklyReviews: await count(db, STORE.weeklyReviews),
      strengthSets: await count(db, STORE.strengthEntries),
      aiProposals: await count(db, STORE.aiProposals),
      trainingSessions: await count(db, STORE.trainingWeeks),
    },
    photoBytes,
  })
}

export interface DeletionOutcome {
  readonly category: DeletionCategory
  readonly recordsRemoved: number
}

export async function deleteCategory(
  db: SolaDatabase,
  category: DeletionCategory,
): Promise<Result<DeletionOutcome, StorageError>> {
  if (category === 'photos') {
    /* Reuses the repository path written at Checkpoint 3, which already removes
       both blobs per photo in one transaction. Re-implementing it here is
       exactly how a second version forgets the thumbnail — the defect D-081
       recorded and the orphan sweep hid. */
    const removed = await deleteAllPhotos(db)
    if (!removed.ok) return removed
    return ok({ category, recordsRemoved: removed.value })
  }

  if (category === 'weeklyReviews') return deleteReviewsWithOpinions(db, category)

  const store = STORE_FOR[category]
  const cleared = await clearStore(db, store)
  if (!cleared.ok) return cleared

  notifyWritten(store)
  return ok({ category, recordsRemoved: cleared.value })
}

const STORE_FOR: Readonly<
  Record<Exclude<DeletionCategory, 'photos' | 'weeklyReviews'>, StoreName>
> = {
  weighIns: STORE.weightEntries,
  measurementSessions: STORE.measurementSessions,
  strengthSets: STORE.strengthEntries,
  aiProposals: STORE.aiProposals,
  trainingSessions: STORE.trainingWeeks,
}

/**
 * Reviews and their opinions, together.
 *
 * An opinion exists only as the answer to a week. Deleting the week and keeping
 * the answer leaves a record nothing points at, which no screen would ever show
 * and no sweep would ever collect — a quiet leak of exactly the health-adjacent
 * text the user just asked to be rid of.
 */
async function deleteReviewsWithOpinions(
  db: SolaDatabase,
  category: DeletionCategory,
): Promise<Result<DeletionOutcome, StorageError>> {
  const written = await withTransaction(db, [STORE.weeklyReviews, STORE.opinions], async (tx) => {
    const reviews = tx.objectStore(STORE.weeklyReviews)
    const removed = await reviews.count()
    await reviews.clear()
    await tx.objectStore(STORE.opinions).clear()
    return removed
  })
  if (!written.ok) return written

  notifyWritten(STORE.weeklyReviews)
  notifyWritten(STORE.opinions)
  return ok({ category, recordsRemoved: written.value })
}

async function clearStore(
  db: SolaDatabase,
  store: StoreName,
): Promise<Result<number, StorageError>> {
  return withTransaction(db, [store], async (tx) => {
    const target = tx.objectStore(store)
    const removed = await target.count()
    await target.clear()
    return removed
  })
}

async function count(db: SolaDatabase, store: StoreName): Promise<number> {
  return db.count(store)
}
